/**
 * Record-composer state and device draft persistence.
 *
 * Financial drafts are isolated by user + Workspace + Book + entry context. A
 * reset clears in-memory state at an auth/workspace boundary without exposing
 * a different Book's saved draft. Persisted drafts are only read when the
 * caller supplies the exact same namespace again. Legacy Company-scoped
 * Control drafts are migrated on first recovery.
 */

import { create } from 'zustand';
import { newIdempotencyKey, type TxKind } from '@budgeting101/api-client';

export type WizardStep = 'amount' | 'details' | 'review';

export interface WizardAmountState {
  /** Exact minor units when `inputMajor` currently parses, otherwise `0`. */
  amountMinor: string;
  /** Explicit transaction currency. */
  currency: string;
  /** User-authored major-unit input, retained exactly across failures. */
  inputMajor?: string;
}

export interface WizardDetailsState {
  fromAccountId?: string;
  toAccountId?: string;
  categoryId?: string;
  customerId?: string;
  spenderAccountId?: string;
  cashHolderAccountId?: string;
  settlementOwnerUserId?: string;
  settlementBoxId?: string;
  date: string;
  memo: string;
  /** Legacy server-entry attachment URLs retained for draft compatibility. */
  attachmentUrls: string[];
  /** Tenant-scoped draft uploads selected for the next preview/commit. */
  draftUploadIds: string[];
}

export interface WizardDraftNamespace {
  userId: string;
  workspaceId: string;
  bookId: string;
  compatibilityCompanyId?: string | null;
  kind: string;
  entryId?: string;
  duplicateFrom?: string;
}

export interface WizardDraftSeed {
  step?: WizardStep;
  amount?: Partial<WizardAmountState>;
  details?: Partial<WizardDetailsState>;
}

interface PersistedWizardDraft {
  version: 1 | 2;
  namespaceKey: string;
  draftId: string;
  kind: string;
  step: WizardStep;
  amount: WizardAmountState;
  details: WizardDetailsState;
  savedAt: string;
}

export interface WizardState {
  open: boolean;
  kind: string | null;
  step: WizardStep;
  amount: WizardAmountState;
  details: WizardDetailsState;
  namespaceKey: string | null;
  draftId: string | null;
  savedAt: string | null;

  beginDraft: (namespace: WizardDraftNamespace, seed?: WizardDraftSeed) => void;
  openWizard: (kind: TxKind) => void;
  closeWizard: () => void;
  setStep: (step: WizardStep) => void;
  setAmount: (amount: WizardAmountState) => void;
  setDetails: (details: Partial<WizardDetailsState>) => void;
  discardDraft: (seed?: WizardDraftSeed) => void;
  /** Clear the active in-memory draft without deleting namespaced recovery data. */
  suspendDraft: () => void;
  /** Clear the active draft and every persisted draft at an identity boundary. */
  purgeDrafts: () => void;
  /** @deprecated Use suspendDraft or purgeDrafts to make the boundary explicit. */
  reset: () => void;
}

const STORAGE_PREFIX = 'budgeting101:record-draft:v2';
const LEGACY_STORAGE_PREFIX = 'budgeting101:record-draft:v1';

/** Remove every device draft at an authentication identity boundary. */
export function purgePersistedWizardDrafts(): void {
  if (!storageAvailable()) return;
  try {
    const keys: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (
        key?.startsWith(`${STORAGE_PREFIX}:`)
        || key?.startsWith(`${LEGACY_STORAGE_PREFIX}:`)
      ) keys.push(key);
    }
    for (const key of keys) window.localStorage.removeItem(key);
  } catch {
    // Browsers may disable storage. In-memory reset still prevents disclosure.
  }
}

function todayOnDevice(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultDetails(date = todayOnDevice()): WizardDetailsState {
  return {
    date,
    memo: '',
    attachmentUrls: [],
    draftUploadIds: [],
  };
}

const defaultAmount: WizardAmountState = {
  amountMinor: '0',
  currency: 'USD',
  inputMajor: '',
};

export function wizardDraftStorageKey(namespace: WizardDraftNamespace): string {
  const context = namespace.entryId
    ? `edit:${namespace.entryId}`
    : namespace.duplicateFrom
      ? `duplicate:${namespace.duplicateFrom}`
      : `new:${namespace.kind}`;
  return [
    STORAGE_PREFIX,
    encodeURIComponent(namespace.userId),
    encodeURIComponent(namespace.workspaceId),
    encodeURIComponent(namespace.bookId),
    encodeURIComponent(context),
  ].join(':');
}

function legacyWizardDraftStorageKey(namespace: WizardDraftNamespace): string | null {
  if (!namespace.compatibilityCompanyId) return null;
  const context = namespace.entryId
    ? `edit:${namespace.entryId}`
    : namespace.duplicateFrom
      ? `duplicate:${namespace.duplicateFrom}`
      : `new:${namespace.kind}`;
  return [
    LEGACY_STORAGE_PREFIX,
    encodeURIComponent(namespace.userId),
    encodeURIComponent(namespace.compatibilityCompanyId),
    encodeURIComponent(context),
  ].join(':');
}

function storageAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function readPersistedDraft(key: string): PersistedWizardDraft | null {
  if (!storageAvailable()) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<PersistedWizardDraft>;
    if (
      (value.version !== 1 && value.version !== 2)
      || value.namespaceKey !== key
      || !isString(value.draftId)
      || !isString(value.kind)
      || !['amount', 'details', 'review'].includes(value.step ?? '')
      || !value.amount
      || !isString(value.amount.amountMinor)
      || !isString(value.amount.currency)
      || (value.amount.inputMajor !== undefined && !isString(value.amount.inputMajor))
      || !value.details
      || !isString(value.details.date)
      || !isString(value.details.memo)
      || !Array.isArray(value.details.attachmentUrls)
      || !value.details.attachmentUrls.every(isString)
      || (value.details.draftUploadIds !== undefined && (
        !Array.isArray(value.details.draftUploadIds)
        || !value.details.draftUploadIds.every(isString)
      ))
      || !isString(value.savedAt)
    ) {
      try { window.localStorage.removeItem(key); } catch { /* Storage is unavailable. */ }
      return null;
    }
    return {
      ...value,
      details: {
        ...value.details,
        draftUploadIds: value.details.draftUploadIds ?? [],
      },
    } as PersistedWizardDraft;
  } catch {
    try { window.localStorage.removeItem(key); } catch { /* Storage is unavailable. */ }
    return null;
  }
}

function writePersistedDraft(state: WizardState): string | null {
  if (!storageAvailable() || !state.namespaceKey || !state.draftId || !state.kind) return null;
  const savedAt = new Date().toISOString();
  const snapshot: PersistedWizardDraft = {
    version: 2,
    namespaceKey: state.namespaceKey,
    draftId: state.draftId,
    kind: state.kind,
    step: state.step,
    amount: state.amount,
    details: state.details,
    savedAt,
  };
  try {
    window.localStorage.setItem(state.namespaceKey, JSON.stringify(snapshot));
    return savedAt;
  } catch {
    return null;
  }
}

function recoverPersistedDraft(namespace: WizardDraftNamespace, key: string): PersistedWizardDraft | null {
  const current = readPersistedDraft(key);
  if (current) return current;

  const legacyKey = legacyWizardDraftStorageKey(namespace);
  if (!legacyKey) return null;
  const legacy = readPersistedDraft(legacyKey);
  if (!legacy || legacy.kind !== namespace.kind) return null;

  const migrated: PersistedWizardDraft = {
    ...legacy,
    version: 2,
    namespaceKey: key,
  };
  if (storageAvailable()) {
    try {
      window.localStorage.setItem(key, JSON.stringify(migrated));
      window.localStorage.removeItem(legacyKey);
    } catch {
      // Recovery remains available in memory. The next field update retries
      // persistence under the canonical key without deleting the legacy copy.
    }
  }
  return migrated;
}

function freshState(kind: string | null, seed: WizardDraftSeed = {}) {
  const date = seed.details?.date ?? todayOnDevice();
  return {
    open: kind !== null,
    kind,
    step: seed.step ?? 'amount' as WizardStep,
    amount: { ...defaultAmount, ...seed.amount },
    details: {
      ...defaultDetails(date),
      ...seed.details,
      attachmentUrls: seed.details?.attachmentUrls ?? [],
      draftUploadIds: seed.details?.draftUploadIds ?? [],
    },
    savedAt: null,
  };
}

export const useWizardStore = create<WizardState>((set, get) => {
  function persistAfterSet(): void {
    const state = get();
    const savedAt = writePersistedDraft(state);
    if (savedAt) set({ savedAt });
  }

  return {
    ...freshState(null),
    namespaceKey: null,
    draftId: null,

    beginDraft(namespace, seed = {}) {
      const key = wizardDraftStorageKey(namespace);
      if (get().namespaceKey === key) return;
      const persisted = recoverPersistedDraft(namespace, key);
      if (persisted && persisted.kind === namespace.kind) {
        set({
          open: true,
          kind: persisted.kind,
          step: persisted.step === 'review' ? 'details' : persisted.step,
          amount: persisted.amount,
          details: persisted.details,
          namespaceKey: key,
          draftId: persisted.draftId,
          savedAt: persisted.savedAt,
        });
        return;
      }
      set({
        ...freshState(namespace.kind, seed),
        namespaceKey: key,
        draftId: newIdempotencyKey(),
      });
    },

    openWizard(kind) {
      set({
        ...freshState(kind),
        namespaceKey: null,
        draftId: null,
      });
    },

    closeWizard() {
      set({ open: false });
    },

    setStep(step) {
      set({ step });
      persistAfterSet();
    },

    setAmount(amount) {
      set({ amount });
      persistAfterSet();
    },

    setDetails(partial) {
      set((state) => ({ details: { ...state.details, ...partial } }));
      persistAfterSet();
    },

    discardDraft(seed = {}) {
      const state = get();
      if (storageAvailable() && state.namespaceKey) {
        try { window.localStorage.removeItem(state.namespaceKey); } catch { /* Storage is unavailable. */ }
      }
      set({
        ...freshState(state.kind, seed),
        namespaceKey: state.namespaceKey,
        draftId: newIdempotencyKey(),
      });
    },

    suspendDraft() {
      set({
        ...freshState(null),
        namespaceKey: null,
        draftId: null,
      });
    },

    purgeDrafts() {
      purgePersistedWizardDrafts();
      set({
        ...freshState(null),
        namespaceKey: null,
        draftId: null,
      });
    },

    reset() {
      get().purgeDrafts();
    },
  };
});
